<script setup lang="ts">
  import { useTranslation } from '@/composables/useTranslation'
  import { inject, ref, computed, type Ref } from 'vue'
  import type { AIChatSession, CustomerCase, AiChatQuestionTemplate } from '@/types/generated'
  import { useRouter } from 'vue-router'
  import { ROUTES } from '@/router/routes'

  // Define emit for closing the popover and prefilling the input
  const emit = defineEmits(['close', 'prefill'])

  // Router for navigation
  const router = useRouter()

  const { t } = useTranslation({
    en: {
      title: 'Prompt Templates',
      gespeicherte: 'Saved',
      allgemein: 'General',
      arbeitsrecht: 'Labor Law',
      insolvenzrecht: 'Insolvency Law',
      mietrecht: 'Tenancy Law',
      noTemplates: 'No templates available',
      manageTemplates: 'Manage templates'
    },
    de: {
      title: 'Fragevorlagen',
      gespeicherte: 'Gespeicherte',
      allgemein: 'Allgemein',
      arbeitsrecht: 'Arbeitsrecht',
      insolvenzrecht: 'Insolvenzrecht',
      mietrecht: 'Mietrecht',
      noTemplates: 'Keine Vorlagen verfügbar',
      manageTemplates: 'Vorlagen verwalten'
    }
  })

  const caseData = inject<Ref<CustomerCase | null>>('caseData', ref(null))
  const sendChatMessage = inject(
    'sendChatMessage',
    async (session: AIChatSession | null, message: string) => {}
  )
  const isSessionGenerating = inject<(uuid: string) => boolean>('isSessionGenerating', () => false)
  const activeChatSession = inject<Ref<AIChatSession | null>>('activeChatSession', ref(null))
  const isActiveSessionGenerating = computed(() => {
    return activeChatSession.value && isSessionGenerating(activeChatSession.value.uuid)
  })

  // For debugging - to check what data is available
  const hasTemplates = computed(() => {
    // The templates might be directly in the org object or in the AI chat session
    return !!caseData?.value?.org?.ai_chat_question_templates?.length
  })

  // Group templates by law field
  const templatesByLawField = computed(() => {
    // Check both possible locations for templates
    const templates = caseData?.value?.org?.ai_chat_question_templates || []

    const grouped: Record<string, AiChatQuestionTemplate[]> = {}

    // Group templates by law field
    templates.forEach((template) => {
      const lawField = template.law_field || 'Allgemein'

      if (!grouped[lawField]) {
        grouped[lawField] = []
      }

      grouped[lawField].push(template)
    })

    // Remove empty categories
    return Object.fromEntries(
      Object.entries(grouped).filter(([_, templates]) => templates.length > 0)
    )
  })

  // Track which sections are expanded
  const expandedSections = ref<Record<string, boolean>>({})

  // Toggle section expansion
  const toggleSection = (lawField: string) => {
    expandedSections.value[lawField] = !expandedSections.value[lawField]
  }

  // Handle selecting a template
  const selectTemplate = (template: AiChatQuestionTemplate) => {
    if (!isActiveSessionGenerating.value) {
      // Instead of sending the message directly, emit an event with the template content
      emit('prefill', template.prompt)
      // Close the popover after prefilling
      emit('close')
    }
  }

  // Get truncated prompt preview
  const getPromptPreview = (prompt: string, maxLength = 120) => {
    if (prompt.length <= maxLength) return prompt
    return prompt.substring(0, maxLength) + '...'
  }

  // Navigate to template settings page
  const goToTemplateSettings = async () => {
    await router.push({
      path: ROUTES.APP.SETTINGS.AI.PROMPT_TEMPLATES,
      replace: true
    })
    emit('close')
  }
</script>

<template>
  <div class="flex w-[450px] flex-col bg-white">
    <!-- Title bar -->
    <div class="border-b border-gray-200 px-4 py-3">
      <h2 class="text-lg font-medium">{{ t('title') }}</h2>
    </div>

    <!-- Scrollable content -->
    <div
      class="overflow-y-auto"
      style="max-height: 450px"
    >
      <!-- Template sections grouped by law field -->
      <div
        v-for="(templates, lawField) in templatesByLawField"
        :key="lawField"
        class="py-1"
      >
        <!-- Section header -->
        <div
          class="flex cursor-pointer items-center justify-between px-4 py-3 hover:bg-gray-50"
          @click="toggleSection(lawField)"
        >
          <h3 class="text-sm font-medium">{{ lawField }}</h3>
          <svg
            class="h-5 w-5 text-gray-500 transition-transform"
            :class="{ 'rotate-180': expandedSections[lawField] }"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 011.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
        </div>

        <!-- Section content -->
        <div
          v-if="expandedSections[lawField]"
          class="space-y-2 px-4 py-2"
        >
          <div
            v-for="template in templates"
            :key="template.uuid"
            class="cursor-pointer rounded-md border p-3 hover:bg-gray-50"
            @click="selectTemplate(template)"
          >
            <div class="flex flex-col">
              <div class="mb-2 text-sm font-medium">
                {{ template.title }}
              </div>
              <div class="line-clamp-3 text-sm text-gray-600">
                {{ getPromptPreview(template.prompt) }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Show message if no templates available -->
      <div
        v-if="Object.keys(templatesByLawField).length === 0"
        class="p-4 text-center text-gray-500"
      >
        {{ t('noTemplates') }}
      </div>

      <!-- Debug info - remove after testing -->
      <div
        v-if="Object.keys(templatesByLawField).length === 0 && hasTemplates"
        class="p-4 text-xs text-gray-400"
      >
        Debug: Templates found but not displayed correctly
      </div>
    </div>

    <!-- Settings link -->
    <div class="border-t border-gray-200 bg-white px-4 py-2">
      <button
        class="flex items-center text-xs text-gray-500 hover:text-gray-700 focus:outline-none"
        @click="goToTemplateSettings"
      >
        <svg
          class="mr-1 h-3 w-3"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path
            d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"
          ></path>
          <circle
            cx="12"
            cy="12"
            r="3"
          ></circle>
        </svg>
        {{ t('manageTemplates') }}
      </button>
    </div>
  </div>
</template>

<style scoped>
  .rotate-180 {
    transform: rotate(180deg);
  }

  .line-clamp-3 {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
</style>
